import { useState, useEffect, useCallback } from 'react';
import learningJourneyService from '../services/learningJourneyService';
import { FEATURE_GATES } from '../config/featureGates';

const STORAGE_KEY = 'loopy-shadowing-professional-mode';
const HEADPHONE_SEEN_KEY = 'loopy-shadowing-headphone-seen';
const LOCKED_DISMISSED_KEY = 'loopy-shadowing-locked-prompt-dismissed';

export function useShadowingMode() {
  const [isProfessionalMode, setIsProfessionalMode] = useState(() => {
    try {
      return localStorage.getItem(STORAGE_KEY) === 'true';
    } catch {
      return false;
    }
  });

  const [isProfessionalAvailable, setIsProfessionalAvailable] = useState(false);
  const [speakingScore, setSpeakingScore] = useState(0);
  const [speakingCefr, setSpeakingCefr] = useState<string>('A1');
  const [isLoading, setIsLoading] = useState(true);
  const [hasSeenHeadphoneTip, setHasSeenHeadphoneTip] = useState(() => {
    try {
      return localStorage.getItem(HEADPHONE_SEEN_KEY) === 'true';
    } catch {
      return false;
    }
  });

  const [hasDismissedLockedPrompt, setHasDismissedLockedPrompt] = useState(() => {
    try {
      return localStorage.getItem(LOCKED_DISMISSED_KEY) === 'true';
    } catch {
      return false;
    }
  });

  // Check speaking score on mount
  useEffect(() => {
    let cancelled = false;
    const checkScore = async () => {
      try {
        const position = await learningJourneyService.getPosition();
        if (cancelled) return;
        const score = position?.dimensions?.speaking?.score ?? 0;
        const level = position?.dimensions?.speaking?.cefr || position?.overallCefr || 'A1';
        setSpeakingScore(score);
        setSpeakingCefr(level);
        setIsProfessionalAvailable(score >= FEATURE_GATES.shadowingProfessional.speakingScore);
      } catch {
        if (!cancelled) setIsProfessionalAvailable(false);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };
    checkScore();
    return () => { cancelled = true; };
  }, []);

  const toggleMode = useCallback(() => {
    setIsProfessionalMode(prev => {
      const next = !prev;
      try {
        localStorage.setItem(STORAGE_KEY, String(next));
      } catch { /* ignore */ }
      return next;
    });
  }, []);

  const dismissHeadphoneTip = useCallback(() => {
    setHasSeenHeadphoneTip(true);
    try {
      localStorage.setItem(HEADPHONE_SEEN_KEY, 'true');
    } catch { /* ignore */ }
  }, []);

  const dismissLockedPrompt = useCallback(() => {
    setHasDismissedLockedPrompt(true);
    try {
      localStorage.setItem(LOCKED_DISMISSED_KEY, 'true');
    } catch { /* ignore */ }
  }, []);

  return {
    isProfessionalMode: isProfessionalMode && isProfessionalAvailable,
    isProfessionalAvailable,
    speakingScore,
    speakingCefr,
    unlockScore: FEATURE_GATES.shadowingProfessional.speakingScore,
    unlockCefr: FEATURE_GATES.shadowingProfessional.speakingCefr,
    toggleMode,
    isLoading,
    hasSeenHeadphoneTip,
    dismissHeadphoneTip,
    hasDismissedLockedPrompt,
    dismissLockedPrompt,
  };
}
